import React, { useState, useRef } from "react";
import { Play, Pause, Volume2, VolumeX } from "lucide-react";
import { AnimatedSection } from "./shared";

const M = `${process.env.REACT_APP_BACKEND_URL}/api/media`;

const videos = [
  { src: `${M}/testimonial-supriya.mp4`, poster: `${M}/testimonial-supriya-poster.jpg`, name: "Supriya", location: "Hyderabad" },
  { src: `${M}/testimonial-1.mp4`, poster: `${M}/testimonial-1-poster.jpg`, name: "Aysha", location: "United States" },
  { src: `${M}/testimonial-2.mp4`, poster: `${M}/testimonial-2-poster.jpg`, name: "Sushmitha", location: "Chennai" },
  { src: `${M}/testimonial-3.mp4`, poster: `${M}/testimonial-3-poster.jpg`, name: "Anusha", location: "Australia" },
  { src: `${M}/testimonial-4.mp4`, poster: `${M}/testimonial-4-poster.jpg`, name: "Pavithra", location: "United States" },
  { src: `${M}/testimonial-5.mp4`, poster: `${M}/testimonial-5-poster.jpg`, name: "Shailaja", location: "Chennai" },
  { src: `${M}/testimonial-6.mp4`, poster: `${M}/testimonial-6-poster.jpg`, name: "Priya", location: "Sharjah" },
  { src: `${M}/testimonial-7.mp4`, poster: `${M}/testimonial-7-poster.jpg`, name: "Sushmitha", location: "Chennai" },
  { src: `${M}/testimonial-8.mp4`, poster: `${M}/testimonial-8-poster.jpg`, name: "Client", location: "India" },
  { src: `${M}/testimonial-9.mp4`, poster: `${M}/testimonial-9-poster.jpg`, name: "Kavya", location: "Bangalore" },
];

const VideoCard = ({ video, idx, mobile }) => {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [loaded, setLoaded] = useState(false);

  const handleLoaded = () => {
    const v = ref.current;
    if (v && !loaded) { v.currentTime = 0.5; setLoaded(true); }
  };

  const toggle = () => {
    const v = ref.current;
    if (!v) return;
    if (playing) { v.pause(); setPlaying(false); }
    else { v.muted = muted; v.play().then(() => setPlaying(true)).catch(() => {}); }
  };

  const toggleMute = (e) => {
    e.stopPropagation();
    const v = ref.current;
    if (!v) return;
    v.muted = !muted;
    setMuted(!muted);
  };

  return (
    <div
      data-testid={`video-testimonial-${idx}`}
      className={`relative group cursor-pointer ${mobile ? "w-full" : "flex-shrink-0 w-[220px] snap-center"}`}
      onClick={toggle}
    >
      <div className="relative aspect-[9/16] rounded-2xl overflow-hidden bg-emerald_deep/20 shadow-lg">
        <video
          ref={ref}
          src={video.src}
          poster={video.poster}
          playsInline
          loop
          muted={muted}
          preload="metadata"
          onLoadedData={handleLoaded}
          className="w-full h-full object-cover"
          data-testid={`video-player-${idx}`}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/20 pointer-events-none" />
        {!playing && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className={`${mobile ? "w-10 h-10" : "w-14 h-14"} rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center border border-white/30 group-hover:scale-110 transition-transform`}>
              <Play size={mobile ? 16 : 22} className="text-white ml-0.5" fill="white" />
            </div>
          </div>
        )}
        {playing && (
          <button onClick={toggleMute} data-testid={`video-mute-${idx}`}
            className="absolute bottom-14 right-3 w-9 h-9 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10">
            {muted ? <VolumeX size={14} className="text-white" /> : <Volume2 size={14} className="text-white" />}
          </button>
        )}
        {playing && (
          <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
            <div className="w-8 h-8 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center">
              <Pause size={12} className="text-white" fill="white" />
            </div>
          </div>
        )}
        <div className="absolute bottom-0 left-0 right-0 px-3 pb-3 pointer-events-none">
          <p className={`text-white font-medium tracking-wide ${mobile ? "text-xs" : "text-sm"}`}>{video.name}</p>
          <p className={`text-white/60 tracking-wider uppercase mt-0.5 ${mobile ? "text-[8px]" : "text-[10px]"}`}>{video.location}</p>
        </div>
      </div>
    </div>
  );
};

const VideoTestimonialsSection = () => (
  <section id="testimonials" data-testid="video-testimonials-section" className="py-20 md:py-28 bg-emerald_deep relative noise-overlay overflow-hidden">
    <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
      <AnimatedSection>
        <div className="text-center mb-12">
          <span className="text-xs tracking-[0.2em] uppercase font-semibold text-accent">Real Stories, Real Results</span>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-tight tracking-tight text-bone mt-3">
            Our Clients <span className="italic font-light">Speak</span>
          </h2>
          <p className="text-bone/60 mt-4 max-w-xl mx-auto text-base">
            Tap to play and hear their journey with Fitly India.
          </p>
        </div>
      </AnimatedSection>

      {/* Mobile: 2-column grid */}
      <div className="grid grid-cols-2 gap-3 sm:hidden" data-testid="video-testimonials-mobile">
        {videos.map((v, idx) => (
          <VideoCard key={v.src} video={v} idx={idx} mobile />
        ))}
      </div>

      {/* Desktop: scrollable row with visible scroll indicator */}
      <div className="hidden sm:block relative max-w-6xl mx-auto">
        <div className="flex gap-5 overflow-x-auto pb-5 snap-x snap-mandatory" style={{ scrollbarWidth: 'thin', scrollbarColor: '#D4AF37 #1A3C34' }} data-testid="video-testimonials-desktop">
          {videos.map((v, idx) => (
            <AnimatedSection key={v.src} delay={idx * 0.06}>
              <VideoCard video={v} idx={idx} />
            </AnimatedSection>
          ))}
        </div>
        <p className="text-bone/30 text-[10px] tracking-[0.15em] uppercase text-center mt-3">
          Scroll to see more →
        </p>
      </div>
    </div>
  </section>
);

export default VideoTestimonialsSection;
